import React from 'react';
import { Col, Form, FormModal, InputNumber, Row, Select } from 'components';
import { PercentageIcon } from 'components/common/icons';
import { ValidationErrors } from 'constants/validations';
import { useSpecialPricingRulesAddEdit } from 'feature/clientTabs/payments/specialPricingRules/addEdit/useSpecialPricingRulesAddEdit';
import { preventNonNumericValue } from 'utils/helpers';

const { Item } = Form;

export interface ISpecialPricingRulesAddUpdateModal {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: Record<string, any>;
}

export const SpecialPricingRulesAddUpdateModal = (props: ISpecialPricingRulesAddUpdateModal) => {
  const { modalState, onModalStateChange, dataToEdit } = props;
  const { form, contextHolder, isLoading, formattedSupplierData, formattedPricingRuleBrandData, onFinish, onCancel } =
    useSpecialPricingRulesAddEdit({
      modalState,
      onModalStateChange,
      dataToEdit,
    });
  return (
    <>
      {contextHolder}
      <FormModal
        form={form}
        title={`${dataToEdit ? 'Update' : 'Add'} Special Pricing Rules`}
        width={680}
        okText={dataToEdit ? 'Save' : 'Add'}
        open={modalState}
        isLoading={isLoading}
        onCancel={onCancel}
        onSubmit={onFinish}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              name={'brandId'}
              label={'Brand*'}
              rules={[{ required: true, message: ValidationErrors.required('Brand') }]}
            >
              <Select size={'large'} options={formattedPricingRuleBrandData} />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              name={'markup'}
              label={'Margin'}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);

                    if (isNaN(num)) {
                      return Promise.reject(new Error('Margin must be a valid number.'));
                    }

                    if (num < 0) {
                      return Promise.reject(new Error('Margin must be a positive number.'));
                    }

                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Margin can have up to 2 decimal points.'));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<PercentageIcon />}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              name={'discount'}
              label={'Discount'}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);

                    if (isNaN(num)) {
                      return Promise.reject(new Error('Discount must be a valid number.'));
                    }

                    if (num < 0) {
                      return Promise.reject(new Error('Discount must be a positive number.'));
                    }

                    if (num >= 100) {
                      return Promise.reject(new Error('Discount must be less than 100.'));
                    }

                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Discount can have up to 2 decimal points.'));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<PercentageIcon />}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label={'Pricing Reference Supplier'}
              shouldUpdate={(prevValues: any, currentValues: any) => prevValues.brandId !== currentValues.brandId}
            >
              {({ getFieldValue }) => (
                <Item name={'pricingReferenceSupplierId'}>
                  <Select disabled={!getFieldValue('brandId')} size="large" options={formattedSupplierData} />
                </Item>
              )}
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label={'Main Supplier'}
              shouldUpdate={(prevValues: any, currentValues: any) => prevValues.brandId !== currentValues.brandId}
            >
              {({ getFieldValue }) => (
                <Item name={'mainSupplierId'}>
                  <Select disabled={!getFieldValue('brandId')} size="large" options={formattedSupplierData} />
                </Item>
              )}
            </Item>
          </Col>
        </Row>
      </FormModal>
    </>
  );
};
